import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { ListingsService, UserListing } from '../services/listings.service';
import { AuthService } from '../services/auth.service';
import { AdminService } from '../services/admin.service';
import { MessagesService } from '../services/messages.service';
import { SearchService, SearchListing } from '../services/search.service';
import { WishlistService } from '../services/wishlist.service';
import { getFormConfig, FormField } from '../utils/form-fields-configs';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { WishlistHeartComponent } from '../wishlist/wishlist-heart/wishlist-heart.component';
import { take } from 'rxjs/operators';
import {
  MessageComposeModalComponent,
  MessageComposeData,
} from '../message-compose-modal/message-compose-modal.component';
import { GoogleMapComponent } from '../google-map/google-map.component';
import { CountdownTimerComponent } from '../countdown-timer/countdown-timer.component';
import {
  VehicleSelectorComponent,
  VehicleSelection,
} from '../vehicle-selector/vehicle-selector.component';
import {
  LocationAutocompleteComponent,
  LocationSelectedEvent,
} from '../location-autocomplete/location-autocomplete.component';
import { MotorcycleBrandSelectorComponent } from '../vehicle-selector/motorcycle-brand-selector.component';

interface VehicleFeature {
  id: number;
  feature_name: string;
  feature_category: string;
  description: string;
}

interface GroupedFeatures {
  [category: string]: VehicleFeature[];
}

interface DateFieldConfig {
  yearRange: {
    start: number;
    end: number;
  };
  maxDate: Date;
}

@Component({
  selector: 'app-listing-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    MessageComposeModalComponent,
    WishlistHeartComponent,
    GoogleMapComponent,
    CountdownTimerComponent,
    VehicleSelectorComponent,
    MotorcycleBrandSelectorComponent,
    LocationAutocompleteComponent,
  ],
  templateUrl: './listing-details.component.html',
  styleUrl: './listing-details.component.css',
})
export class ListingDetailsComponent implements OnInit, OnDestroy {
  listing: UserListing | null = null;
  loading = true;
  error: string | null = null;
  formFields: FormField[] = [];
  listingData: any = {};
  currentImageIndex = 0;
  listingImages: string[] = [];
  isLoggedIn = false;
  isAdmin = false;
  adminProcessing = false;

  touchStartX?: number;
  touchStartY?: number;
  touchStartTime?: number;
  showCarouselControls = false;
  controlsTimeout?: any;
  hasShownControls = false;
  isTouchScrolling?: boolean;
  touchMoved?: boolean;
  lastTouchX?: number;
  lastTouchY?: number;

  private readonly SWIPE_THRESHOLD = 30;
  private readonly SWIPE_FAST_THRESHOLD = 20;
  private readonly SWIPE_VELOCITY_THRESHOLD = 0.3;
  private readonly SWIPE_TIME_THRESHOLD = 600;
  private readonly VERTICAL_SCROLL_THRESHOLD = 25;
  private readonly TAP_MOVEMENT_THRESHOLD = 10;
  private readonly TAP_TIME_THRESHOLD = 300;

  isEditMode = false;
  editFormData: any = {};
  editForm!: FormGroup;
  editLoading = false;
  originalListingData: any = null;
  listingFeatures: VehicleFeature[] = [];
  groupedFeatures: GroupedFeatures = {};
  hasFeatures = false;
  hasLocationForMap = false;
  locationForMap: string = '';
  suggestedListings: any[] = [];
  isLoadingSuggested = false;
  showShareMenu = false;
  suggestedListingsError: string | null = null;
  private isUpdatingPowerFields = false;

  private dateFieldsConfig: { [fieldName: string]: DateFieldConfig } = {
    first_registration: {
      yearRange: { start: 1950, end: new Date().getFullYear() },
      maxDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    },
    registered_until: {
      yearRange: {
        start: new Date().getFullYear(),
        end: new Date().getFullYear() + 1,
      },
      maxDate: new Date(new Date().getFullYear() + 1, new Date().getMonth(), 1),
    },
  };

  dateFieldsData: {
    [key: string]: {
      month: string;
      year: string;
    };
  } = {};

  dateFieldYearOptions: { [fieldName: string]: number[] } = {};

  monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  private fieldSectionsCache: { [key: string]: FormField[] } | null = null;
  private fieldEditableCache: Map<string, boolean> = new Map();
  private subscriptions: Subscription[] = [];
  private readonly defaultPlaceholderImage =
    '/assets/images/listing-placeholder/no-image-placeholder.png';

  deleteConfirmation: {
    show: boolean;
    deleting: boolean;
  } = {
    show: false,
    deleting: false,
  };

  imageModal: {
    show: boolean;
    currentIndex: number;
    images: string[];
    touchStartX?: number;
    touchStartY?: number;
    initialTouchCount?: number;
  } = {
    show: false,
    currentIndex: 0,
    images: [],
    touchStartX: undefined,
    touchStartY: undefined,
    initialTouchCount: undefined,
  };

  adminActionModal: {
    show: boolean;
    action: 'approve' | 'decline';
    reason: string;
    note: string;
    processing: boolean;
  } = {
    show: false,
    action: 'approve',
    reason: '',
    note: '',
    processing: false,
  };

  messages: {
    success: string | null;
    error: string | null;
  } = {
    success: null,
    error: null,
  };

  messageModal: {
    show: boolean;
    data: MessageComposeData | null;
  } = {
    show: false,
    data: null,
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private listingsService: ListingsService,
    private authService: AuthService,
    private adminService: AdminService,
    private messagesService: MessagesService,
    private searchService: SearchService,
    private wishlistService: WishlistService,
    private cdr: ChangeDetectorRef,
    private formBuilder: FormBuilder
  ) {}

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.imageModal.show) {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.closeImageModal();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.previousModalImage();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.nextModalImage();
          break;
      }
    }
  }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadListingFromRoute();
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.resetBodyOverflow();
  }

  private initializeSubscriptions(): void {
    const authSub = this.authService.isLoggedIn$.subscribe(
      (loggedIn: boolean) => {
        this.isLoggedIn = loggedIn;
      }
    );
    this.subscriptions.push(authSub);

    const userSub = this.authService.currentUser$.subscribe((user) => {
      this.isAdmin = user?.is_admin === true;
    });
    this.subscriptions.push(userSub);
  }

  private loadListingFromRoute(): void {
    const routeSub = this.route.params.subscribe((params) => {
      const listingId = params['id'];
      const subcategory = params['subcategory'];

      if (listingId && subcategory) {
        this.loadListingDetails(listingId, subcategory);
      } else {
        this.error = 'Invalid listing parameters provided';
        this.loading = false;
      }
    });
    this.subscriptions.push(routeSub);
  }

  private cleanupSubscriptions(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private resetBodyOverflow(): void {
    document.body.style.overflow = 'auto';
  }

  loadListingDetails(listingId: string, subcategory: string): void {
    this.loading = true;
    this.error = null;

    this.listingsService.getListingDetails(listingId, subcategory).subscribe({
      next: (listing) => {
        this.listing = listing;
        this.listingData = listing;
        this.setupImages();
        this.setupFormFields();
        this.setupFeatures();
        this.setupLocationForMap();
        this.loading = false;

        if (!this.isOwnerListing()) {
          this.loadSuggestedListings();
        }
      },
      error: (error) => {
        console.error('Error loading listing details:', error);
        this.error =
          error.message ||
          'Failed to load listing details. Please try again later.';
        this.loading = false;
      },
    });
  }

  private setupLocationForMap(): void {
    if (!this.listing) return;

    const locationFields = ['city', 'location', 'address'];

    for (const field of locationFields) {
      const value = this.getFieldValue(field);
      if (value && value.trim()) {
        this.locationForMap = value.trim();
        this.hasLocationForMap = true;
        return;
      }
    }

    this.hasLocationForMap = false;
    this.locationForMap = '';
  }

  getLocationDisplayName(): string {
    const locationFields = ['city', 'location', 'address'];

    for (const field of locationFields) {
      const value = this.getFieldValue(field);
      if (value && value.trim()) {
        return value.trim();
      }
    }

    return 'Location not specified';
  }

  private setupFeatures(): void {
    if (
      this.listing &&
      this.listing.features &&
      Array.isArray(this.listing.features)
    ) {
      this.listingFeatures = this.listing.features;
      this.hasFeatures = this.listingFeatures.length > 0;

      if (this.hasFeatures) {
        this.groupedFeatures = this.groupFeaturesByCategory(
          this.listingFeatures
        );
      }
    } else {
      this.resetFeatures();
    }
  }

  private resetFeatures(): void {
    this.listingFeatures = [];
    this.hasFeatures = false;
    this.groupedFeatures = {};
  }

  private groupFeaturesByCategory(features: VehicleFeature[]): GroupedFeatures {
    const grouped: GroupedFeatures = {};

    features.forEach((feature) => {
      const category = feature.feature_category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(feature);
    });

    Object.keys(grouped).forEach((category) => {
      grouped[category].sort((a, b) =>
        a.feature_name.localeCompare(b.feature_name)
      );
    });

    return grouped;
  }

  formatCategoryName(categoryKey: string): string {
    return categoryKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getFeatureCategories(): string[] {
    return Object.keys(this.groupedFeatures).sort();
  }

  getFeaturesInCategory(category: string): VehicleFeature[] {
    return this.groupedFeatures[category] || [];
  }

  isVehicleCategory(): boolean {
    if (!this.listing?.table_name) return false;

    const vehicleKeywords = [
      'vehicle',
      'automobile',
      'motorcycle',
      'commercial',
      'oldtimer',
      'nautical',
      'spare',
      'campers',
      'crashed',
      'construction',
      'agriculture',
    ];

    return vehicleKeywords.some((keyword) =>
      this.listing!.table_name.includes(keyword)
    );
  }

  private loadSuggestedListings(): void {
    if (!this.listing?.id) return;

    this.isLoadingSuggested = true;
    this.suggestedListingsError = null;

    const category = this.getCategoryFromTableName(this.listing.table_name);
    const subcategory = this.getSubcategoryFromTableName(
      this.listing.table_name
    );

    this.authService.currentUser$.pipe(take(1)).subscribe((currentUser) => {
      const currentUserId = currentUser?.id;

      this.searchService
        .getSuggestedListings(
          this.listing!.id,
          category,
          subcategory,
          4,
          currentUserId
        )
        .subscribe({
          next: (response) => {
            this.suggestedListings = response.suggestions.map((listing) =>
              this.convertSearchListingToDisplay(listing)
            );
            this.isLoadingSuggested = false;
          },
          error: (error) => {
            this.suggestedListingsError = 'Unable to load similar listings';
            this.isLoadingSuggested = false;
            this.suggestedListings = [];
          },
        });
    });
  }

  shouldShowDeclinedMessage(): boolean {
    return (
      this.listing?.status === 'declined' &&
      (this.isOwnerListing() || this.isAdmin)
    );
  }

  shouldShowExpiryInfo(): boolean {
    if (!this.listing?.expires_at) return false;

    if (
      this.listing.status === 'pending' ||
      this.listing.status === 'declined'
    ) {
      return false;
    }
    return this.isOwnerListing() || this.isAdmin;
  }

  shouldShowActionsButtons(): boolean {
    if (!this.listing) return false;
    if (this.isAdmin) {
      return true;
    }

    return this.isOwnerListing() || this.isAdmin;
  }

  canShowListingActions(): boolean {
    if (this.isAdmin) {
      return (
        this.listing?.status === 'pending' ||
        this.listing?.status === 'declined'
      );
    }
    if (this.isOwnerListing()) {
      const allowedStatuses = ['active', 'pending', 'declined'];
      return this.listing?.status
        ? allowedStatuses.includes(this.listing.status)
        : false;
    }
    return false;
  }

  shouldShowPendingExpiryMessage(): boolean {
    return (
      this.listing?.status === 'pending' &&
      (this.isOwnerListing() || this.isAdmin)
    );
  }

  getListingStatusText(): string {
    if (!this.listing) return 'Unknown';

    if (this.listing.status === 'pending') {
      return 'Pending Approval';
    }

    if (this.listing.status === 'active') {
      return this.isListingExpired() ? 'Expired' : 'Active';
    }

    return (
      this.listing.status.charAt(0).toUpperCase() + this.listing.status.slice(1)
    );
  }

  isListingExpired(): boolean {
    if (!this.listing?.expires_at) return false;

    if (this.listing.status === 'pending') return false;

    const now = new Date();
    const expirationDate = new Date(this.listing.expires_at);

    const placeholderDate = new Date('2099-12-31');
    if (expirationDate.getTime() === placeholderDate.getTime()) return false;

    return now > expirationDate;
  }

  getExpirationDisplayText(): string {
    if (!this.listing) return '';

    if (this.listing.status === 'pending') {
      return 'Will be set once approved';
    }

    if (this.isListingExpired()) {
      return this.formatExpirationDate(this.listing.expires_at);
    }

    return this.getTimeUntilExpiration();
  }

  getExpirationStatusClass(): string {
    if (!this.listing) return '';

    if (this.listing.status === 'pending') {
      return 'pending-info';
    }

    if (this.listing.status === 'declined') {
      return 'declined-info';
    }

    if (this.isListingExpired()) {
      return 'expired-info';
    } else if (this.isExpiringSoon()) {
      return 'expiring-soon';
    } else {
      return 'active-info';
    }
  }

  toggleShareMenu(): void {
    this.showShareMenu = !this.showShareMenu;
  }

  copyLink(): void {
    const link = window.location.href;
    navigator.clipboard.writeText(link).then(() => {
      this.showShareMenu = false;
    });
  }

  getWhatsAppShareUrl(): string {
    return `https://wa.me/?text=${encodeURIComponent(
      'Check out this listing: ' + window.location.href
    )}`;
  }

  getFacebookShareUrl(): string {
    return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      window.location.href
    )}`;
  }

  getTwitterShareUrl(): string {
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      'Check out this listing: ' + window.location.href
    )}`;
  }

  shouldShowSeconds(): boolean {
    if (!this.listing?.expires_at) return false;
    if (this.listing.status === 'pending') return false;

    const now = new Date();
    const expirationDate = new Date(this.listing.expires_at);
    const timeDiff = expirationDate.getTime() - now.getTime();
    const hoursRemaining = timeDiff / (1000 * 60 * 60);

    return hoursRemaining <= 24 && hoursRemaining > 0;
  }

  formatExpirationDate(dateString: string | undefined): string {
    if (!dateString) return 'Invalid date';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid date';
    }
  }

  getTimeUntilExpiration(): string {
    if (!this.listing?.expires_at) return '';
    if (this.listing.status === 'pending') return '';

    const now = new Date();
    const expirationDate = new Date(this.listing.expires_at);
    const timeDiff = expirationDate.getTime() - now.getTime();

    if (timeDiff <= 0) return 'Expired';

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );

    if (days > 0) {
      return `${days} day${days === 1 ? '' : 's'} remaining`;
    } else if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} remaining`;
    } else {
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      return `${minutes} minute${minutes === 1 ? '' : 's'} remaining`;
    }
  }

  isExpiringSoon(): boolean {
    if (!this.listing?.expires_at) return false;

    if (this.listing.status === 'pending') return false;

    const now = new Date();
    const expirationDate = new Date(this.listing.expires_at);
    const timeDiff = expirationDate.getTime() - now.getTime();
    const hoursRemaining = timeDiff / (1000 * 60 * 60);

    return hoursRemaining > 0 && hoursRemaining <= 24;
  }

  shouldShowExpirationInfo(): boolean {
    if (!this.listing?.expires_at) return false;

    if (
      this.listing.status === 'pending' &&
      !this.isAdmin &&
      !this.isOwnerListing()
    ) {
      return false;
    }

    return this.isOwnerListing() || this.isAdmin;
  }

  getPendingStatusMessage(): string {
    if (this.listing?.status === 'pending') {
      return 'Listing is pending admin approval';
    }
    return '';
  }

  private convertSearchListingToDisplay(searchListing: SearchListing): any {
    return {
      id: searchListing.id,
      title: searchListing.title,
      price: searchListing.price,
      currency: searchListing.currency,
      location: searchListing.location || 'Location not specified',
      category: this.formatCategoryNameForDisplay(searchListing.category),
      subcategory: searchListing.subcategory,
      image: searchListing.first_image || this.defaultPlaceholderImage,
      table_name: this.getTableNameFromSearchListing(searchListing),
      created_at: searchListing.created_at,
      is_featured: searchListing.is_featured,
    };
  }

  private formatCategoryNameForDisplay(category: string): string {
    const categoryMap: { [key: string]: string } = {
      vehicles: 'Vehicles',
      'real-estate': 'Real Estate',
      marketplace: 'Marketplace',
      jobs: 'Jobs',
    };
    return categoryMap[category] || category;
  }

  private getTableNameFromSearchListing(listing: SearchListing): string {
    const tableMap: { [key: string]: string } = {
      'vehicles-cars': 'attributes_cars',
      'vehicles-motorcycles': 'attributes_motorcycles',
      'vehicles-automobile': 'attributes_automobile',
      'vehicles-motorcycle': 'attributes_motorcycle',
      'vehicles-commercial-vehicles': 'attributes_commercial_vehicles',
      'vehicles-oldtimer': 'attributes_oldtimer',
      'vehicles-nautical': 'attributes_nautical',
      'vehicles-spare-parts': 'attributes_spare_parts',
      'vehicles-campers-caravans': 'attributes_campers_caravans',
      'vehicles-crashed-cars': 'attributes_crashed_cars',
      'vehicles-construction': 'attributes_construction',
      'vehicles-agriculture': 'attributes_agriculture',
      'real-estate-house-sale': 'attributes_house_sale',
      'real-estate-house-rent': 'attributes_house_rent',
      'real-estate-apartments-sale': 'attributes_apartments_sale',
      'real-estate-apartments-rent': 'attributes_apartments_rent',
      'real-estate-apartment-rent': 'attributes_apartment_rent',
      'real-estate-land-sale': 'attributes_land_sale',
      'real-estate-land-rent': 'attributes_land_rent',
      'real-estate-business-office-sale': 'attributes_business_office_sale',
      'real-estate-business-office-rent': 'attributes_business_office_rent',
      'real-estate-garage-sale': 'attributes_garage_sale',
      'real-estate-garage-rent': 'attributes_garage_rent',
      'real-estate-luxury-real-estate-sale':
        'attributes_luxury_real_estate_sale',
      'real-estate-luxury-real-estate-rent':
        'attributes_luxury_real_estate_rent',
      'real-estate-room-rent': 'attributes_room_rent',
      'real-estate-looking-for-roommate': 'attributes_roommate',
      'marketplace-home': 'attributes_home',
      'marketplace-electronics': 'attributes_electronics',
      'marketplace-sport': 'attributes_sport',
      'marketplace-children': 'attributes_children',
      'marketplace-head-to-toe': 'attributes_head_to_toe',
      'marketplace-machines-tools': 'attributes_machines_tools',
      'marketplace-media': 'attributes_media',
      'marketplace-musical-instruments': 'attributes_musical_instruments',
      'marketplace-literature': 'attributes_literature',
      'marketplace-pets': 'attributes_pets',
      'marketplace-other': 'attributes_other',
      'marketplace-giving-away': 'attributes_giving_away',
      'jobs-professional': 'attributes_professional',
      'jobs-home-maintenance': 'attributes_home_maintenance',
      'jobs-personal-care-wellness': 'attributes_personal_care_wellness',
      'jobs-events-entertainment': 'attributes_events_entertainment',
      'jobs-education-training': 'attributes_education_training',
      'jobs-transport-delivery': 'attributes_transport_delivery',
    };

    const key = `${listing.category}-${listing.subcategory}`;
    return (
      tableMap[key] || `attributes_${listing.subcategory.replace(/-/g, '_')}`
    );
  }

  private getCategoryFromTableName(tableName: string): string {
    const categoryMappings = [
      {
        keywords: [
          'vehicles',
          'cars',
          'motorcycles',
          'automobile',
          'motorcycle',
        ],
        category: 'vehicles',
      },
      {
        keywords: ['real_estate', 'house', 'apartment'],
        category: 'real-estate',
      },
      {
        keywords: ['marketplace', 'electronics', 'home'],
        category: 'marketplace',
      },
      { keywords: ['jobs', 'professional'], category: 'jobs' },
    ];

    for (const mapping of categoryMappings) {
      if (mapping.keywords.some((keyword) => tableName.includes(keyword))) {
        return mapping.category;
      }
    }

    return 'marketplace';
  }

  private getSubcategoryFromTableName(tableName: string): string {
    const parts = tableName.replace('attributes_', '').split('_');
    return parts.join('-');
  }

  onSuggestedListingClick(listing: any): void {
    if (listing.subcategory) {
      this.router.navigate([
        '/listing-details',
        listing.subcategory,
        listing.id,
      ]);
    }
  }

  formatSuggestedListingPrice(listing: any): string {
    if (listing.price === 0) return 'Free';

    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: listing.currency || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
      return formatter.format(listing.price);
    } catch (error) {
      return `${listing.price} ${listing.currency || 'EUR'}`;
    }
  }

  formatSuggestedListingDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'Unknown date';
    }
  }

  canUseWishlist(): boolean {
    return this.wishlistService.canUseWishlist();
  }

  setupImages(): void {
    if (this.listing?.images && Array.isArray(this.listing.images)) {
      this.listingImages = this.listing.images;
    } else if (this.listing?.first_image) {
      this.listingImages = [this.listing.first_image];
    } else {
      this.listingImages = [this.defaultPlaceholderImage];
    }
    this.currentImageIndex = 0;
  }

  setupFormFields(): void {
    if (!this.listing?.table_name) return;

    try {
      const category = this.getCategoryName(this.listing.table_name);
      const subcategory = this.getSubcategoryName(this.listing.table_name);

      const categoryMap: { [key: string]: string } = {
        'Real Estate': 'real-estate',
        Vehicles: 'vehicles',
        Marketplace: 'marketplace',
        Jobs: 'jobs',
      };

      const formCategory = categoryMap[category] || category.toLowerCase();
      this.formFields = getFormConfig(formCategory, subcategory);
      this.clearCaches();
    } catch (error) {
      console.error('Error setting up form fields:', error);
      this.formFields = [];
    }
  }

  messageSeller(): void {
    if (!this.validateMessagingConditions()) return;

    this.messageModal = {
      show: true,
      data: {
        listingId: this.listing!.id.toString(),
        listingTableName: this.listing!.table_name,
        sellerId: this.listing!.user_id!.toString(),
        sellerName: this.getOwnerName(),
        listingTitle: this.listing!.title || 'Unknown Listing',
        listingPrice: this.getMainPrice(),
      },
    };
  }

  private validateMessagingConditions(): boolean {
    if (!this.isUserLoggedIn()) {
      this.showLoginPrompt();
      return false;
    }

    if (!this.listing) {
      this.showErrorMessage('Listing information not available.');
      return false;
    }

    if (this.isOwnerListing()) {
      this.showErrorMessage('You cannot message yourself.');
      return false;
    }

    if (!this.listing.user_id) {
      this.showErrorMessage(
        'Unable to contact seller. User information not available.'
      );
      return false;
    }

    if (this.isAdmin) {
      this.showErrorMessage('Admins cannot send messages to users.');
      return false;
    }

    if (this.listing.status !== 'active') {
      this.showErrorMessage(
        'You can only message sellers for active listings.'
      );
      return false;
    }

    return true;
  }

  onSendFirstMessage(messageText: string): void {
    if (!this.messageModal.data) {
      this.closeMessageModal();
      return;
    }

    this.messagesService
      .sendFirstMessage({
        listingId: this.messageModal.data.listingId,
        listingTableName: this.messageModal.data.listingTableName,
        sellerId: this.messageModal.data.sellerId,
        message: messageText,
      })
      .subscribe({
        next: (response) => {
          this.closeMessageModal();
          this.showSuccessMessage('Message sent successfully!');
          this.router.navigate(['/messages', response.conversation.id]);
        },
        error: (error) => {
          console.error('Error sending first message:', error);
          this.showErrorMessage(
            error.message || 'Failed to send message. Please try again.'
          );
        },
      });
  }

  closeMessageModal(): void {
    this.messageModal = {
      show: false,
      data: null,
    };
  }

  canMessageSeller(): boolean {
    return (
      this.isUserLoggedIn() &&
      !this.isOwnerListing() &&
      !this.isAdmin &&
      this.listing?.status === 'active' &&
      !!this.listing?.user_id
    );
  }

  canApprove(): boolean {
    return !!(
      this.isAdmin &&
      this.listing &&
      this.listing.status &&
      ['pending', 'declined'].includes(this.listing.status)
    );
  }

  canDecline(): boolean {
    return !!(
      this.isAdmin &&
      this.listing &&
      this.listing.status &&
      ['pending', 'active'].includes(this.listing.status)
    );
  }

  showApproveModal(): void {
    this.adminActionModal = {
      show: true,
      action: 'approve',
      reason: '',
      note: '',
      processing: false,
    };
  }

  showDeclineModal(): void {
    this.adminActionModal = {
      show: true,
      action: 'decline',
      reason: '',
      note: '',
      processing: false,
    };
  }

  closeAdminActionModal(): void {
    this.adminActionModal = {
      show: false,
      action: 'approve',
      reason: '',
      note: '',
      processing: false,
    };
  }

  confirmAdminAction(): void {
    if (!this.listing || this.adminActionModal.processing) return;

    if (
      this.adminActionModal.action === 'decline' &&
      !this.isDeclineReasonValid()
    ) {
      this.showErrorMessage(
        'Please provide at least 20 characters explaining the reason for decline.'
      );
      return;
    }

    this.adminActionModal.processing = true;
    const listingId = this.listing.id.toString();

    if (this.adminActionModal.action === 'approve') {
      this.handleApprovalAction(listingId);
    } else {
      this.handleDeclineAction(listingId);
    }
  }

  isDeclineReasonValid(): boolean {
    return this.adminActionModal.reason.trim().length >= 20;
  }

  private handleApprovalAction(listingId: string): void {
    this.adminService
      .approveListings([listingId], this.adminActionModal.note)
      .subscribe({
        next: (response) => {
          this.showSuccessMessage('Listing approved successfully!');
          this.updateListingStatus('active');
          this.closeAdminActionModal();
        },
        error: (error) => {
          console.error('Error approving listing:', error);
          this.showErrorMessage(error.message || 'Failed to approve listing');
          this.adminActionModal.processing = false;
        },
      });
  }

  private handleDeclineAction(listingId: string): void {
    this.adminService
      .declineListings([listingId], this.adminActionModal.reason.trim())
      .subscribe({
        next: (response) => {
          let message = 'Listing declined successfully!';
          const result = response.results.find((r) => r.id === listingId);
          if (result?.relist_refunded && result.refund_info) {
            message += ' Free relist credit refunded.';
          }
          this.showSuccessMessage(message);
          this.updateListingStatus('declined');
          this.closeAdminActionModal();
        },
        error: (error) => {
          console.error('Error declining listing:', error);
          this.showErrorMessage(error.message || 'Failed to decline listing');
          this.adminActionModal.processing = false;
        },
      });
  }

  private updateListingStatus(newStatus: string): void {
    if (this.listing) {
      this.listing.status = newStatus;
      if (newStatus === 'declined' && this.adminActionModal.reason) {
        (this.listing as any).decline_reason = this.adminActionModal.reason;
      } else if (newStatus === 'active') {
        delete (this.listing as any).decline_reason;
      }
    }
  }

  canEdit(): boolean {
    return !!(
      this.isAdmin &&
      this.listing &&
      this.listing.status === 'pending' &&
      !this.isEditMode
    );
  }

  enableEditMode(): void {
    if (!this.canEdit()) return;
    this.originalListingData = this.deepCloneListingData(this.listingData);
    this.editFormData = {};
    this.buildEditableFieldsCache();
    this.initAllDateFieldYearOptions();
    const formControls: { [key: string]: any } = {};
    for (const field of this.formFields) {
      if (this.isFieldEditableCached(field.name)) {
        if (field.type === 'vehicle-selector' && field.name === 'vehicle') {
          const brand = this.getFieldValue('brand') || '';
          const model = this.getFieldValue('model') || '';
          const vehicleValue = { brand, model };
          const validators = this.getFieldValidators(field);
          formControls[field.name] = [vehicleValue, validators];
        } else if (this.isCustomDropdownDateField(field.name)) {
          const value = this.getFieldValue(field.name);
          let formValue = '';
          if (value) {
            const match = value.match(/^(\d{2})\/(\d{4})$/);
            if (match) {
              const month = match[1];
              const year = match[2];
              this.dateFieldsData[field.name] = {
                month: parseInt(month, 10).toString(),
                year: year,
              };
              formValue = value;
            }
          }
          const validators = this.getFieldValidators(field);
          formControls[field.name] = [formValue, validators];
        } else {
          const value = this.getFieldValue(field.name);
          let formValue: any;
          if (field.type === 'checkbox') {
            formValue = value === true || value === 'true';
          } else if (field.type === 'number') {
            formValue = value !== null && value !== undefined ? value : null;
          } else if (field.type === 'date') {
            formValue = this.convertToDateInputFormat(value);
          } else {
            formValue = value !== null && value !== undefined ? value : '';
          }
          const validators = this.getFieldValidators(field);
          formControls[field.name] = [formValue, validators];
        }
      }
    }
    this.editForm = this.formBuilder.group(formControls);
    this.setupPowerFieldsConversion();
    this.isEditMode = true;
    this.showSuccessMessage(
      'Edit mode enabled. Make your changes and click Save.'
    );
    this.cdr.markForCheck();
  }

  private getFieldValidators(field: FormField): any[] {
    const validators: any[] = [];
    if (field.validators && field.validators.length > 0) {
      validators.push(...field.validators);
    }
    return validators;
  }

  private convertToDateInputFormat(value: any): string {
    if (!value) return '';

    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error converting date to input format:', error);
      return '';
    }
  }

  private normalizeValue(value: any): any {
    if (value === null || value === undefined) return null;

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const numValue = parseFloat(value);
      return isNaN(numValue) ? value : numValue;
    }

    return value;
  }

  private deepCloneListingData(data: any): any {
    if (data === null || typeof data !== 'object') return data;
    if (Array.isArray(data)) {
      return data.map((item) => this.deepCloneListingData(item));
    }
    const cloned: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        cloned[key] = this.deepCloneListingData(data[key]);
      }
    }
    return cloned;
  }

  private buildEditableFieldsCache(): void {
    this.fieldEditableCache.clear();
    const protectedFields = [
      'id',
      'user_id',
      'status',
      'created_at',
      'view_count',
    ];
    for (const field of this.formFields) {
      const isEditable = !protectedFields.includes(field.name);
      this.fieldEditableCache.set(field.name, isEditable);
    }
  }

  private clearCaches(): void {
    this.fieldSectionsCache = null;
    this.fieldEditableCache.clear();
  }

  cancelEditMode(): void {
    if (!this.isEditMode) return;
    if (this.hasUnsavedChanges()) {
      if (
        !confirm('You have unsaved changes. Are you sure you want to cancel?')
      ) {
        return;
      }
    }
    if (this.originalListingData) {
      this.listingData = this.originalListingData;
      this.listing = this.originalListingData;
    }
    this.isEditMode = false;
    this.editFormData = {};
    this.originalListingData = null;
    if (this.editForm) {
      this.editForm.reset();
    }
    this.clearCaches();
    this.dismissMessage('success');
  }

  saveEditedListing(): void {
    if (!this.listing || !this.isEditMode || this.editLoading) return;
    if (this.editForm.invalid) {
      this.markFormGroupTouched(this.editForm);
      this.showErrorMessage('Please fix the validation errors before saving.');
      return;
    }
    if (!this.hasUnsavedChanges()) {
      this.showErrorMessage('No changes to save.');
      return;
    }
    const updates: any = {};
    const formValue = this.editForm.value;
    Object.keys(formValue).forEach((key) => {
      if (key === 'vehicle' && typeof formValue[key] === 'object') {
        const vehicleValue = formValue[key];
        const currentBrand = vehicleValue.brand || '';
        const currentModel = vehicleValue.model || '';
        const existingBrand = this.getFieldValue('brand') || '';
        const existingModel = this.getFieldValue('model') || '';
        if (currentBrand !== existingBrand || currentModel !== existingModel) {
          updates['brand'] = currentBrand;
          updates['model'] = currentModel;
        }
        return;
      }
      const originalValue = this.getFieldValue(key);
      let editedValue = formValue[key];

      if (editedValue === '' || editedValue === undefined) {
        editedValue = null;
      }

      const originalNormalized = this.normalizeValue(originalValue);
      const editedNormalized = this.normalizeValue(editedValue);

      if (
        JSON.stringify(originalNormalized) !== JSON.stringify(editedNormalized)
      ) {
        updates[key] = editedValue;
      }
    });
    if (Object.keys(updates).length === 0) {
      this.showErrorMessage('No changes detected.');
      return;
    }
    this.editLoading = true;
    const category = this.getCategoryFromRoute();
    const subcategory = this.getSubcategoryFromRoute();
    const listingId = this.listing.id.toString();
    this.adminService
      .editPendingListing(category, subcategory, listingId, updates)
      .subscribe({
        next: (response) => {
          this.showSuccessMessage('Listing updated successfully!');
          if (response.listing) {
            this.listing = { ...this.listing, ...response.listing };
            this.listingData = this.listing;
          }
          this.isEditMode = false;
          this.editFormData = {};
          this.originalListingData = null;
          this.editLoading = false;
          if (this.editForm) {
            this.editForm.reset();
          }
          this.clearCaches();
          this.setupFormFields();
        },
        error: (error) => {
          console.error('Error updating listing:', error);
          this.showErrorMessage(error.message || 'Failed to update listing');
          this.editLoading = false;
        },
      });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  isFieldEditable(field: FormField): boolean {
    return this.isFieldEditableCached(field.name);
  }

  isAutomobile(tableName: string): boolean {
    return this.getSubcategoryName(tableName) === 'automobile';
  }

  isMotorcycle(tableName: string): boolean {
    return this.getSubcategoryName(tableName) === 'motorcycle';
  }

  private isFieldEditableCached(fieldName: string): boolean {
    if (this.fieldEditableCache.has(fieldName)) {
      return this.fieldEditableCache.get(fieldName)!;
    }
    const protectedFields = [
      'id',
      'user_id',
      'status',
      'created_at',
      'view_count',
    ];
    const isEditable = !protectedFields.includes(fieldName);
    this.fieldEditableCache.set(fieldName, isEditable);
    return isEditable;
  }

  getEditValue(fieldName: string): any {
    return this.editFormData[fieldName];
  }

  isFieldInvalid(fieldName: string): boolean {
    if (!this.editForm) return false;
    const field = this.editForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(field: FormField): string {
    if (!this.editForm) return '';
    const control = this.editForm.get(field.name);
    if (!control || !control.errors) return '';
    const errors = control.errors;
    if (errors['required']) {
      return `${field.label} is required`;
    }
    if (errors['incompleteVehicle']) {
      return 'Please select both brand and model';
    }
    if (errors['vehicleTooShort']) {
      return 'Brand and model must be at least 2 characters';
    }
    if (errors['email']) {
      return 'Please enter a valid email address';
    }
    if (errors['minlength']) {
      return `${field.label} must be at least ${errors['minlength'].requiredLength} characters`;
    }
    if (errors['maxlength']) {
      return `${field.label} must not exceed ${errors['maxlength'].requiredLength} characters`;
    }
    if (errors['min']) {
      return `${field.label} must be at least ${errors['min'].min}`;
    }
    if (errors['max']) {
      return `${field.label} must not exceed ${errors['max'].max}`;
    }
    if (errors['notInteger']) {
      return `${field.label} must be a whole number`;
    }
    if (errors['pattern']) {
      return `${field.label} has an invalid format`;
    }
    if (errors['futureDate']) {
      return 'Date cannot be in the future';
    }
    return 'Invalid value';
  }

  hasUnsavedChanges(): boolean {
    if (!this.isEditMode || !this.originalListingData || !this.editForm)
      return false;
    const formValue = this.editForm.value;
    return Object.keys(formValue).some((key) => {
      const originalValue = this.originalListingData[key];
      const editedValue = formValue[key];
      return JSON.stringify(originalValue) !== JSON.stringify(editedValue);
    });
  }

  private getCategoryFromRoute(): string {
    const url = this.router.url;
    if (url.includes('/vehicles/')) return 'vehicles';
    if (url.includes('/real-estate/')) return 'real_estate';
    if (url.includes('/marketplace/')) return 'marketplace';
    if (url.includes('/jobs/')) return 'jobs';
    return 'vehicles';
  }

  private getSubcategoryFromRoute(): string {
    return this.route.snapshot.params['subcategory'] || '';
  }

  private showSuccessMessage(message: string): void {
    this.messages.success = message;
    this.messages.error = null;
    setTimeout(() => (this.messages.success = null), 5000);
  }

  private showErrorMessage(message: string): void {
    this.messages.error = message;
    this.messages.success = null;
    setTimeout(() => (this.messages.error = null), 8000);
  }

  dismissMessage(type: 'success' | 'error'): void {
    this.messages[type] = null;
  }

  openImageModal(index: number = this.currentImageIndex): void {
    if (this.listingImages.length === 0) return;

    this.imageModal = {
      show: true,
      currentIndex: index,
      images: [...this.listingImages],
    };

    document.body.style.overflow = 'hidden';
  }

  closeImageModal(): void {
    this.imageModal.show = false;
    this.resetBodyOverflow();
  }

  nextModalImage(): void {
    if (this.imageModal.images.length > 1) {
      this.imageModal.currentIndex =
        (this.imageModal.currentIndex + 1) % this.imageModal.images.length;
    }
  }

  previousModalImage(): void {
    if (this.imageModal.images.length > 1) {
      this.imageModal.currentIndex =
        this.imageModal.currentIndex === 0
          ? this.imageModal.images.length - 1
          : this.imageModal.currentIndex - 1;
    }
  }

  selectModalImage(index: number): void {
    if (index >= 0 && index < this.imageModal.images.length) {
      this.imageModal.currentIndex = index;
    }
  }

  getCurrentModalImage(): string {
    return (
      this.imageModal.images[this.imageModal.currentIndex] ||
      this.defaultPlaceholderImage
    );
  }

  getCategoryName(tableName: string): string {
    if (!tableName) return '';
    return this.listingsService.getCategoryFromTableName(tableName);
  }

  getSubcategoryName(tableName?: string): string {
    if (!tableName) return '';
    return this.listingsService.getSubcategoryFromTableName(tableName);
  }

  getFieldValue(fieldName: string): any {
    if (fieldName === 'vehicle') {
      const brand = this.listingData['brand'];
      const model = this.listingData['model'];

      if (brand && model) {
        return { brand, model };
      } else if (brand || model) {
        return { brand: brand || '', model: model || '' };
      }
      return null;
    }

    return this.listingData[fieldName];
  }

  hasFieldValue(fieldName: string): boolean {
    if (fieldName === 'vehicle') {
      const brand = this.listingData['brand'];
      const model = this.listingData['model'];
      return !!(brand || model);
    }

    const value = this.getFieldValue(fieldName);
    return value !== null && value !== undefined && value !== '';
  }

  shouldShowField(field: FormField): boolean {
    return true;
  }

  isDescriptionField(field: FormField): boolean {
    return field.name === 'description' || field.type === 'textarea';
  }

  formatFieldValue(field: FormField, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Not specified';
    }

    switch (field.type) {
      case 'vehicle-selector':
        return this.formatVehicleValue(value);
      case 'checkbox':
        return value ? 'Yes' : 'No';
      case 'select':
        return this.formatSelectValue(field, value);
      case 'number':
        return this.formatNumberValue(field, value);
      case 'date':
        return this.formatDate(value);
      case 'first_registration_dropdown':
      case 'registered_until_dropdown':
        return this.formatCustomDateValue(value);
      default:
        return value.toString();
    }
  }

  private formatCustomDateValue(value: any): string {
    if (!value) return 'Not specified';

    const match = value.match(/^(\d{2})\/(\d{4})$/);
    if (match) {
      const month = parseInt(match[1], 10);
      const year = match[2];
      const monthName =
        this.monthOptions.find((m) => parseInt(m.value) === month)?.label || '';
      return `${monthName} ${year}`;
    }

    return value.toString();
  }

  private formatVehicleValue(value: any): string {
    if (typeof value === 'object' && (value.brand || value.model)) {
      const brand = value.brand || 'Unknown Brand';
      const model = value.model || 'Unknown Model';
      return `${brand} ${model}`;
    }
    return 'Not specified';
  }

  private formatSelectValue(field: FormField, value: any): string {
    if (field.options) {
      const option = field.options.find((opt) => opt.value === value);
      return option ? option.label : value;
    }
    return value;
  }

  private formatNumberValue(field: FormField, value: any): string {
    if (value === null || value === undefined || value === '') {
      return 'Not specified';
    }

    if (field.name.includes('price') || field.name.includes('cost')) {
      return this.formatPrice(value, this.listing?.currency);
    }

    if (
      field.name === 'engine_size' ||
      field.label?.toLowerCase().includes('engine')
    ) {
      const num = parseFloat(value);
      return !isNaN(num) ? num.toFixed(1) : value.toString();
    }

    if (field.step && field.step < 1) {
      return parseFloat(Number(value).toFixed(2)).toString();
    }

    return value.toString();
  }

  formatPrice(price: number, currency?: string): string {
    if (price === 0) return 'Free';

    try {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return formatter.format(price);
    } catch (error) {
      return `${price} ${currency || 'EUR'}`;
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Unknown date';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInDays = Math.floor(
        (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Yesterday';
      if (diffInDays < 30) return `${diffInDays} days ago`;

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'Unknown date';
    }
  }

  getMainPrice(): string {
    if (this.listing?.price !== undefined) {
      return this.formatPrice(this.listing.price, this.listing.currency);
    }

    const priceFields = [
      'monthly_rent',
      'price_min',
      'price_max',
      'budget_max',
      'base_price',
    ];
    for (const field of priceFields) {
      const value = this.getFieldValue(field);
      if (value !== null && value !== undefined) {
        return this.formatPrice(value, this.listing?.currency);
      }
    }

    return 'Price not specified';
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      active: 'status-active',
      declined: 'status-declined',
      pending: 'status-pending',
      deleted: 'status-deleted',
    };

    return statusClasses[status.toLowerCase()] || 'status-default';
  }

  getListingImages(): string[] {
    return this.listingImages;
  }

  getCurrentImage(): string {
    return (
      this.listingImages[this.currentImageIndex] || this.defaultPlaceholderImage
    );
  }

  onTouchStart(event: TouchEvent): void {
    if (this.listingImages.length <= 1) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
    this.touchStartTime = Date.now();
    this.isTouchScrolling = false;
    this.touchMoved = false;
  }

  onTouchMove(event: TouchEvent): void {
    if (this.listingImages.length <= 1) return;
    if (!this.touchStartX || !this.touchStartY) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartX);
    const deltaY = Math.abs(touch.clientY - this.touchStartY);

    if (
      deltaY > this.TAP_MOVEMENT_THRESHOLD ||
      deltaX > this.TAP_MOVEMENT_THRESHOLD
    ) {
      this.touchMoved = true;
    }

    if (deltaY > deltaX && deltaY > this.VERTICAL_SCROLL_THRESHOLD) {
      this.isTouchScrolling = true;
    } else if (deltaX > deltaY && deltaX > 5 && this.listingImages.length > 1) {
      event.preventDefault();
    }

    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.listingImages.length <= 1) {
      this.resetTouchState();
      return;
    }

    if (!this.touchStartX || !this.touchStartY || !this.touchStartTime) {
      return;
    }

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    const deltaTime = Date.now() - this.touchStartTime;

    if (
      this.isTouchScrolling ||
      (absDeltaY > this.VERTICAL_SCROLL_THRESHOLD && absDeltaY > absDeltaX)
    ) {
      this.resetTouchState();
      return;
    }

    const velocity = deltaTime > 0 ? absDeltaX / deltaTime : 0;

    const isHorizontalSwipe = absDeltaX > absDeltaY;
    const isQuickEnough = deltaTime < this.SWIPE_TIME_THRESHOLD;

    const isFastSwipe =
      velocity >= this.SWIPE_VELOCITY_THRESHOLD &&
      absDeltaX >= this.SWIPE_FAST_THRESHOLD;
    const isLongEnough = absDeltaX > this.SWIPE_THRESHOLD || isFastSwipe;

    const isNotTooVertical = absDeltaY < this.VERTICAL_SCROLL_THRESHOLD;

    if (
      isHorizontalSwipe &&
      isQuickEnough &&
      isLongEnough &&
      isNotTooVertical
    ) {
      event.stopPropagation();
      event.preventDefault();

      if (deltaX > 0) {
        this.previousImage();
      } else {
        this.nextImage();
      }
    }

    this.resetTouchState();
  }

  private resetTouchState(): void {
    this.touchStartX = undefined;
    this.touchStartY = undefined;
    this.touchStartTime = undefined;
    this.isTouchScrolling = false;
    this.touchMoved = false;
    this.lastTouchX = undefined;
    this.lastTouchY = undefined;
  }

  onImageZoneClick(event: Event, zone: 'prev' | 'next' | 'view'): void {
    event.stopPropagation();

    if (zone === 'prev') {
      this.previousImage();
    } else if (zone === 'next') {
      this.nextImage();
    }
  }

  onMobileImageClick(event: Event): void {
    if (window.innerWidth <= 1024) {
      this.openImageModal();
    }
  }

  onImageHover(): void {
    if (this.listingImages.length <= 1) return;

    if (!this.hasShownControls) {
      this.showCarouselControls = true;
      this.hasShownControls = true;

      if (this.controlsTimeout) {
        clearTimeout(this.controlsTimeout);
      }

      this.controlsTimeout = setTimeout(() => {
        this.showCarouselControls = false;
      }, 2000);
    }
  }

  onImageLeave(): void {
    if (this.controlsTimeout) {
      clearTimeout(this.controlsTimeout);
    }
    this.hasShownControls = false;
    this.showCarouselControls = false;
  }

  selectImage(index: number): void {
    if (index >= 0 && index < this.listingImages.length) {
      this.currentImageIndex = index;
    }
  }

  nextImage(): void {
    if (this.listingImages.length > 1) {
      this.currentImageIndex =
        (this.currentImageIndex + 1) % this.listingImages.length;
    }
  }

  previousImage(): void {
    if (this.listingImages.length > 1) {
      this.currentImageIndex =
        this.currentImageIndex === 0
          ? this.listingImages.length - 1
          : this.currentImageIndex - 1;
    }
  }

  hasMultipleImages(): boolean {
    return this.listingImages.length > 1;
  }

  getSectionIcon(sectionName: string): string {
    const iconMap: { [key: string]: string } = {
      'Basic Information': '📋',
      Pricing: '💰',
      Details: '📝',
      Specifications: '⚙️',
      'Additional Details': '📄',
      Features: '🔧',
    };
    return iconMap[sectionName] || '📄';
  }

  goBack(): void {
    this.location.back();
  }

  getFieldSections(): { [key: string]: FormField[] } {
    if (this.fieldSectionsCache !== null) {
      return this.fieldSectionsCache;
    }
    const sections: { [key: string]: FormField[] } = {
      'Basic Information': [],
      Pricing: [],
      Details: [],
    };

    this.formFields.forEach((field) => {
      if (['title', 'description'].includes(field.name)) {
        sections['Basic Information'].push(field);
      } else if (this.isPricingField(field.name)) {
        sections['Pricing'].push(field);
      } else {
        sections['Details'].push(field);
      }
    });

    Object.keys(sections).forEach((key) => {
      if (sections[key].length === 0) {
        delete sections[key];
      }
    });

    this.fieldSectionsCache = sections;
    return sections;
  }

  private isPricingField(fieldName: string): boolean {
    const pricingFields = ['price', 'cost', 'rent', 'currency'];
    return pricingFields.some((field) => fieldName.includes(field));
  }

  isOwnerListing(): boolean {
    if (!this.listing || !this.isLoggedIn) return false;

    if (this.listing.is_owner !== undefined) {
      return this.listing.is_owner;
    }

    if (this.listing.user_id) {
      return this.authService.isListingOwner(this.listing.user_id);
    }

    if (this.listing.email) {
      return this.authService.isListingOwnerByDetails(this.listing.email);
    }

    return false;
  }

  isUserLoggedIn(): boolean {
    return this.isLoggedIn;
  }

  onDeleteListing(): void {
    if (!this.validateOwnerAction()) return;

    this.deleteConfirmation = {
      show: true,
      deleting: false,
    };
  }

  private validateOwnerAction(): boolean {
    if (!this.isOwnerListing()) {
      this.showErrorMessage('You can only modify your own listings.');
      return false;
    }

    if (this.isListingExpired()) {
      this.showErrorMessage(
        'Cannot modify expired listings. Please contact support.'
      );
      return false;
    }

    return true;
  }

  confirmDelete(): void {
    if (!this.listing || !this.isOwnerListing()) {
      this.cancelDelete();
      return;
    }

    this.deleteConfirmation.deleting = true;

    this.listingsService.deleteUserListing(this.listing).subscribe({
      next: (response) => {
        this.showSuccessMessage('Listing deleted successfully!');
        setTimeout(() => {
          this.router.navigate(['/my-listings']);
        }, 1500);
      },
      error: (error) => {
        console.error('Error deleting listing:', error);
        this.showErrorMessage(
          error.message || 'Failed to delete listing. Please try again.'
        );
        this.deleteConfirmation.deleting = false;
      },
    });
  }

  cancelDelete(): void {
    this.deleteConfirmation = {
      show: false,
      deleting: false,
    };
  }

  editListing(): void {
    if (!this.validateOwnerAction()) return;
    this.router.navigate(['/edit-listing', this.listing!.id]);
  }

  getOwnerName(): string {
    if (!this.listing) return '';

    const firstName = this.listing.first_name || '';
    const lastName = this.listing.last_name || '';

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return 'Anonymous Seller';
    }
  }

  showLoginPrompt(): void {
    const message =
      'Please log in to access premium features and contact sellers.\n\n' +
      'Would you like to go to the login page?';

    if (confirm(message)) {
      this.router.navigate(['/login']);
    }
  }

  trackByFieldName(index: number, field: FormField): string {
    return field.name;
  }

  onVehicleSelectionChange(selection: VehicleSelection): void {
    if (this.isEditMode) {
      this.editFormData['brand'] = selection.brand;
      this.editFormData['model'] = selection.model;
    }
  }

  onLocationSelected(event: LocationSelectedEvent): void {
    if (this.isEditMode) {
      this.editFormData['location'] = event.formatted;
      if (event.city) {
        this.editFormData['city'] = event.city;
      }
    }
  }

  getVehicleSelection(): VehicleSelection {
    if (
      this.editFormData['vehicle'] &&
      typeof this.editFormData['vehicle'] === 'object'
    ) {
      return {
        brand: this.editFormData['vehicle'].brand || '',
        model: this.editFormData['vehicle'].model || '',
      };
    }

    return {
      brand: this.editFormData['brand'] || '',
      model: this.editFormData['model'] || '',
    };
  }

  isVehicleSelectorField(fieldName: string): boolean {
    return (
      fieldName === 'brand' || fieldName === 'model' || fieldName === 'vehicle'
    );
  }

  isLocationField(fieldName: string): boolean {
    return fieldName === 'location' || fieldName === 'city';
  }

  shouldShowVehicleSelector(field: FormField): boolean {
    if (
      this.isEditMode &&
      this.isFieldEditable(field) &&
      field.type === 'vehicle-selector'
    ) {
      return true;
    }

    return (
      this.isEditMode &&
      this.isFieldEditable(field) &&
      field.name === 'brand' &&
      this.formFields.some((f) => f.name === 'model')
    );
  }
  shouldShowMotorcycleSelector(field: FormField): boolean {
    if (
      this.isEditMode &&
      this.isFieldEditable(field) &&
      field.type === 'motorcycle-brand-selector'
    ) {
      return true;
    }

    return (
      this.isEditMode &&
      this.isFieldEditable(field) &&
      field.name === 'brand' &&
      this.formFields.some((f) => f.name === 'model')
    );
  }

  shouldHideField(field: FormField): boolean {
    if (!this.isEditMode) return false;

    if (
      field.name === 'model' &&
      this.formFields.some((f) => f.name === 'brand')
    ) {
      return true;
    }

    if (field.name === 'vehicle' && field.type === 'vehicle-selector') {
      return false;
    }

    return false;
  }

  trackByImageIndex(index: number, image: string): number {
    return index;
  }

  trackBySectionKey(index: number, item: any): string {
    return item.key;
  }

  trackByOptionValue(index: number, option: any): any {
    return option.value;
  }

  trackByCategory(index: number, category: string): string {
    return category;
  }

  trackByFeatureId(index: number, feature: VehicleFeature): number {
    return feature.id;
  }

  trackByListingId(index: number, listing: any): any {
    return listing.id;
  }

  preventNumberScroll(event: WheelEvent): void {
    const target = event.target as HTMLInputElement;
    if (target && target.type === 'number') {
      target.blur();
    }
  }

  private setupPowerFieldsConversion(): void {
    const powerKwControl = this.editForm.get('power_kw');
    const powerHpControl = this.editForm.get('power_hp');

    if (powerKwControl && powerHpControl) {
      powerKwControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const hp = this.convertKwToHp(parseFloat(value));
          if (!isNaN(hp)) {
            powerHpControl.setValue(Math.round(hp), { emitEvent: false });
          }
          this.isUpdatingPowerFields = false;
        }
      });

      powerHpControl.valueChanges.subscribe((value) => {
        if (!this.isUpdatingPowerFields && value !== null && value !== '') {
          this.isUpdatingPowerFields = true;
          const kw = this.convertHpToKw(parseFloat(value));
          if (!isNaN(kw)) {
            powerKwControl.setValue(Math.round(kw), { emitEvent: false });
          }
          this.isUpdatingPowerFields = false;
        }
      });
    }
  }

  private isCustomDropdownDateField(fieldName: string): boolean {
    return (
      fieldName === 'first_registration' || fieldName === 'registered_until'
    );
  }

  private initAllDateFieldYearOptions(): void {
    Object.keys(this.dateFieldsConfig).forEach((fieldName) => {
      this.dateFieldYearOptions[fieldName] =
        this.generateYearOptions(fieldName);
    });
  }

  private generateYearOptions(fieldName: string): number[] {
    const config = this.dateFieldsConfig[fieldName];
    if (!config) return [];

    const years: number[] = [];
    for (
      let year = config.yearRange.end;
      year >= config.yearRange.start;
      year--
    ) {
      years.push(year);
    }
    return years;
  }

  getYearOptionsForField(fieldName: string): number[] {
    return this.dateFieldYearOptions[fieldName] || [];
  }

  isMonthDisabled(monthValue: string, fieldName: string): boolean {
    const data = this.dateFieldsData[fieldName];
    const config = this.dateFieldsConfig[fieldName];

    if (!data || !data.year || !config) return false;

    const selectedYear = parseInt(data.year, 10);
    const selectedMonth = parseInt(monthValue, 10);
    const maxDate = config.maxDate;

    const selectedDate = new Date(selectedYear, selectedMonth - 1, 1);

    return selectedDate > maxDate;
  }

  onDateMonthChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].month = select.value;
    this.updateDateValue(fieldName);
  }

  onDateYearChange(event: Event, fieldName: string): void {
    const select = event.target as HTMLSelectElement;

    if (!this.dateFieldsData[fieldName]) {
      this.dateFieldsData[fieldName] = { month: '', year: '' };
    }

    this.dateFieldsData[fieldName].year = select.value;

    const month = this.dateFieldsData[fieldName].month;
    if (month && this.isMonthDisabled(month, fieldName)) {
      this.dateFieldsData[fieldName].month = '';
      const monthSelect = document.getElementById(
        fieldName + '_month'
      ) as HTMLSelectElement;
      if (monthSelect) monthSelect.value = '';
    }

    this.updateDateValue(fieldName);
  }

  private updateDateValue(fieldName: string): void {
    const data = this.dateFieldsData[fieldName];
    if (!data) return;

    const { month, year } = data;

    if (month && year) {
      const paddedMonth = month.padStart(2, '0');
      const dateString = `${paddedMonth}/${year}`;
      this.editForm.get(fieldName)?.setValue(dateString);
      this.editForm.get(fieldName)?.markAsTouched();
    } else {
      this.editForm.get(fieldName)?.setValue('');
    }
  }

  private convertKwToHp(kw: number): number {
    return kw * 1.35962162;
  }

  private convertHpToKw(hp: number): number {
    return hp * 0.73549875;
  }

  onModalTouchStart(event: TouchEvent): void {
    this.imageModal.initialTouchCount = event.touches.length;

    if (event.touches.length === 1) {
      this.imageModal.touchStartX = event.touches[0].clientX;
      this.imageModal.touchStartY = event.touches[0].clientY;
    } else {
      this.imageModal.touchStartX = undefined;
      this.imageModal.touchStartY = undefined;
    }
  }

  onModalTouchMove(event: TouchEvent): void {
    if (event.touches.length !== this.imageModal.initialTouchCount) {
      this.imageModal.touchStartX = undefined;
      this.imageModal.touchStartY = undefined;
      return;
    }

    if (
      event.touches.length === 1 &&
      this.imageModal.touchStartX !== undefined
    ) {
      event.preventDefault();
    }
  }

  onModalTouchEnd(event: TouchEvent): void {
    if (
      this.imageModal.initialTouchCount !== 1 ||
      this.imageModal.touchStartX === undefined ||
      this.imageModal.touchStartY === undefined
    ) {
      this.imageModal.touchStartX = undefined;
      this.imageModal.touchStartY = undefined;
      this.imageModal.initialTouchCount = undefined;
      return;
    }

    const touchEndX = event.changedTouches[0].clientX;
    const touchEndY = event.changedTouches[0].clientY;

    const deltaX = touchEndX - this.imageModal.touchStartX;
    const deltaY = touchEndY - this.imageModal.touchStartY;

    if (
      Math.abs(deltaX) > Math.abs(deltaY) &&
      Math.abs(deltaX) > this.SWIPE_THRESHOLD
    ) {
      if (deltaX > 0) {
        this.previousModalImage();
      } else {
        this.nextModalImage();
      }
    }

    this.imageModal.touchStartX = undefined;
    this.imageModal.touchStartY = undefined;
    this.imageModal.initialTouchCount = undefined;
  }

  isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}
